import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Mail, MapPin } from 'lucide-react';

export default function CGUPage() {
  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="cgu-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3 mb-8">
          <div className="flex items-center justify-center gap-3">
            <FileText className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
              Conditions Générales d'Utilisation
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm">
            Application UrgenceMed - OpenMeta
          </p>
        </div>

        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardContent className="p-6 md:p-8 space-y-6 text-sm leading-relaxed">
            
            {/* Avertissement */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="font-semibold text-amber-300 mb-2">⚠️ Avertissement Important</p>
              <p className="text-[hsl(var(--text-dim))]">
                Toute utilisation de l'Application est soumise au préalable à la prise de connaissance et à l'acceptation des présentes Conditions Générales d'Utilisation.
              </p>
            </div>

            {/* Mises en garde générales */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">Mises en Garde Générales</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>L'application permet aux Utilisateurs d'accéder à une base de données, et grâce à un dispositif d'intelligence artificielle, de comparer les images de la base avec celles prises par l'Utilisateur.</p>
                
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                  <p className="font-semibold text-red-300 mb-2">L'utilisateur est informé et reconnaît que l'application, ses services et contenus ne constituent pas un dispositif médical et ne permettent en aucun cas de bénéficier ou de délivrer :</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>un deuxième avis ;</li>
                    <li>un acte de téléexpertise, au sens des dispositions de l'article R.6316-1 du Code de la Santé Publique.</li>
                  </ul>
                </div>

                <p className="font-semibold">L'utilisateur reconnaît et accepte qu'en tout état de cause il assume seul l'entière responsabilité des décisions, notamment médicales, prises à partir de l'utilisation de l'application et/ou des images contenues dans la base de données ainsi proposées.</p>
                
                <p>La présente application Pro est réservée à l'usage des professionnels de santé, étudiants et médecins. Cependant, la version simplifiée (culture et conseils) est pour tout public.</p>
              </div>
            </section>

            {/* 1. Mentions légales */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">1. Mentions Légales</h2>
              
              <h3 className="text-lg font-semibold text-cyan-200 mb-2">1.1. Éditeur de l'Application</h3>
              <div className="space-y-2 text-[hsl(var(--text-dim))] mb-4">
                <p>OpenMeta, pour la recherche et l'innovation</p>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 text-cyan-400" />
                  <p>Centre de l'innovation technologique – 61330 Céaucé, FRANCE</p>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="w-4 h-4 mt-1 text-cyan-400" />
                  <p>Email : <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline">support@openmeta.fr</a></p>
                </div>
                <p>Directeur de la publication : Dr Yanis Belhatri</p>
              </div>

              <h3 className="text-lg font-semibold text-cyan-200 mb-2">1.2. Hébergeur de l'Application</h3>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>OVH, société par actions simplifiées</p>
                <p>SIREN : 424761419</p>
                <p>Siège social : 2 rue Kellerman, 59100 ROUBAIX, FRANCE</p>
              </div>
            </section>

            {/* 2. Objet et Définitions */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">2. Objet et Définitions</h2>
              <div className="space-y-2 text-[hsl(var(--text-dim))]">
                <p>Les Conditions Générales d'Utilisation sont destinées à encadrer l'utilisation de l'Application.</p>
                <p>Elles ont pour objet de définir les règles d'utilisation de l'Application ainsi que les droits et obligations respectifs des Utilisateurs de l'Application et de l'Éditeur.</p>
                
                <div className="mt-4 space-y-2">
                  <p><strong className="text-[hsl(var(--text))]">« Application »</strong> désigne l'application mobile éditée par OpenMeta, donnant accès à des Services et Contenus après création d'un compte sécurisé ;</p>
                  <p><strong className="text-[hsl(var(--text))]">« Base de données »</strong> désigne l'ensemble des textes, images mises à disposition des utilisateurs au sein de l'Application ;</p>
                  <p><strong className="text-[hsl(var(--text))]">« CGU »</strong> désigne les présentes Conditions générales d'utilisation de l'Application ;</p>
                  <p><strong className="text-[hsl(var(--text))]">« Compte Utilisateur »</strong> désigne le compte permettant à l'Utilisateur d'accéder aux Services et Contenus de l'Application ;</p>
                  <p><strong className="text-[hsl(var(--text))]">« Contenus »</strong> désigne l'ensemble des textes, photographies, vidéos, illustrations, fonctionnalités, la Base de données et plus largement tout élément existant au sein de l'Application.</p>
                </div>
              </div>
            </section>

            {/* 3. Documents contractuels */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">3. Documents Contractuels</h2>
              <p className="text-[hsl(var(--text-dim))]">
                Les présentes CGU forment l'intégralité de la relation contractuelle entre l'Utilisateur et l'Éditeur pour la fourniture des Services et Contenus dans le cadre de l'Application.
              </p>
            </section>

            {/* 4. Entrée en vigueur et durée */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">4. Entrée en Vigueur et Durée</h2>
              <p className="text-[hsl(var(--text-dim))]">
                Les présentes CGU entrent en vigueur sans réserve à l'égard de l'Utilisateur au jour de son acceptation des présentes via le dispositif proposé au sein de l'Application.
              </p>
            </section>

            {/* 5. Conditions d'accès et d'utilisation */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">5. Conditions d'Accès et d'Utilisation</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>L'accès à l'Application et son utilisation par tout Utilisateur sont soumis aux présentes CGU, aux lois en vigueur concernant les services de communication électronique au public, et plus généralement au respect de toutes les lois applicables.</p>
                
                <h3 className="text-lg font-semibold text-cyan-200 mt-4 mb-2">5.1. Configuration Obligatoire</h3>
                <p>L'utilisation de l'Application nécessite de disposer de :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Android 4.4 (ou supérieur) ou iOS 11 (ou supérieur)</li>
                </ul>
                <p>Elle est disponible sur les stores Google Play et App Store.</p>

                <h3 className="text-lg font-semibold text-cyan-200 mt-4 mb-2">5.2. Création d'un Compte Utilisateur</h3>
                <p>L'Utilisateur souhaitant utiliser l'Application est informé que son accès ainsi qu'à l'ensemble des Services et Contenus proposés est subordonné à la création et à la validation d'un Compte Utilisateur.</p>

                <h3 className="text-lg font-semibold text-cyan-200 mt-4 mb-2">5.3. Connexion au Compte Utilisateur</h3>
                <p>À chaque connexion, l'Utilisateur devra s'authentifier par l'intermédiaire du dispositif d'authentification prévu. L'Utilisateur s'engage à garder secrets ses éléments d'authentification.</p>
              </div>
            </section>

            {/* 6. Présentation de l'Application */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">6. Présentation de l'Application, des Services et Contenus</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>L'Application propose de mettre à disposition des Utilisateurs une base de connaissances.</p>
                <p>L'Application donne accès aux fonctionnalités suivantes :</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Base de données</li>
                  <li>Moteur de recherche</li>
                  <li>Chat IA médical</li>
                  <li>Calculateurs de dose</li>
                  <li>Protocoles d'urgence</li>
                </ul>
              </div>
            </section>

            {/* 7. Obligations et responsabilité */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">7. Obligations et Responsabilité</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>L'Éditeur est tenu à une obligation de moyens dans le cadre de la mise à disposition de l'Application et de la fourniture de ses Services et Contenus.</p>
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                  <p className="font-semibold text-amber-300">RAPPEL :</p>
                  <p>L'utilisateur est informé et reconnaît que l'application et ses contenus ne constituent pas un dispositif médical et ne permettent en aucun cas de bénéficier ou de délivrer un deuxième avis ou un acte de télé-expertise.</p>
                </div>
              </div>
            </section>

            {/* 10. Propriété intellectuelle */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">10. Propriété Intellectuelle</h2>
              <p className="text-[hsl(var(--text-dim))]">
                L'Éditeur est titulaire exclusif des droits de propriété intellectuelle et industrielle sur l'Application. L'utilisation de l'Application ne confère en aucun cas aux Utilisateurs un droit de propriété sur l'Application, ses Contenus et notamment sa Base de données.
              </p>
            </section>

            {/* 12. Protection des données */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">12. Protection des Données à Caractère Personnel</h2>
              <div className="space-y-3 text-[hsl(var(--text-dim))]">
                <p>Des données à caractère personnel concernant l'Utilisateur sont collectées et traitées dans le cadre de l'accès et de l'utilisation de l'Application, conformément à la législation applicable relative à la protection des données à caractère personnel (RGPD).</p>
                <p>Pour exercer vos droits (accès, rectification, effacement, portabilité, opposition), contactez :</p>
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline">support@openmeta.fr</a>
                  </p>
                </div>
              </div>
            </section>

            {/* 16. Loi française */}
            <section>
              <h2 className="text-xl font-bold text-cyan-300 mb-3">16. Loi Française</h2>
              <p className="text-[hsl(var(--text-dim))]">
                L'Application a été conçue pour des Utilisateurs français. Les présentes CGU sont régies par la loi française. À défaut de règlement amiable, tout litige relatif à l'Application sera soumis aux tribunaux français.
              </p>
            </section>

            {/* Contact */}
            <section className="pt-4 border-t border-[hsl(var(--border))]">
              <h2 className="text-xl font-bold text-cyan-300 mb-3">Contact</h2>
              <p className="text-[hsl(var(--text-dim))]">
                Pour toute question concernant les présentes CGU, vous pouvez nous contacter à :
              </p>
              <div className="mt-2 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded">
                <a href="mailto:support@openmeta.fr" className="text-cyan-400 hover:underline flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  support@openmeta.fr
                </a>
              </div>
            </section>

            <p className="text-xs text-[hsl(var(--text-dim))] text-center pt-6">
              Dernière mise à jour : Décembre 2024
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
